// src/store/guardians/actions.js

import {
  SET_GUARDIAN_STATE,
  SET_GUARDIAN_HYDRATED,
  UNLOCK_GUARDIAN,
  SET_SELECTED_GUARDIAN,
  SET_GUARDIAN_PURCHASING,
  SET_GUARDIAN_ERROR,
  RESET_GUARDIANS,
} from './actionTypes';
import {
  fetchGuardianState,
  purchaseGuardian as purchaseGuardianApi,
  saveGuardianSelection,
} from 'utils/guardiansService';
import {
  GUARDIAN_NONE_ID,
  DEFAULT_UNLOCKED_GUARDIAN_IDS,
  getGuardianById,
  isKnownGuardianId,
} from 'config/guardiansCatalog';

export const setGuardianState = payload => ({
  type: SET_GUARDIAN_STATE,
  payload,
});

export const setGuardianHydrated = value => ({
  type: SET_GUARDIAN_HYDRATED,
  payload: value,
});

export const unlockGuardian = guardianId => ({
  type: UNLOCK_GUARDIAN,
  payload: guardianId,
});

export const setSelectedGuardian = guardianId => ({
  type: SET_SELECTED_GUARDIAN,
  payload: guardianId,
});

export const setGuardianPurchasing = guardianId => ({
  type: SET_GUARDIAN_PURCHASING,
  payload: guardianId,
});

export const setGuardianError = error => ({
  type: SET_GUARDIAN_ERROR,
  payload: error,
});

export const resetGuardians = () => ({type: RESET_GUARDIANS});

/**
 * Load owned/selected guardians for this user.
 *
 * The service is a stub today and returns null, which leaves the defaults in
 * place. Once it is backed by the database this starts returning real state and
 * nothing here needs to change.
 */
export const hydrateGuardians = userId => async dispatch => {
  try {
    const stored = await fetchGuardianState(userId);
    if (!stored) {
      dispatch(setGuardianHydrated(true));
      return;
    }

    // Defensively drop ids this build doesn't know about, so a guardian that
    // was removed from the catalog can't wedge the selection screen.
    const unlockedIds = Array.from(
      new Set([
        ...DEFAULT_UNLOCKED_GUARDIAN_IDS,
        ...(Array.isArray(stored.unlockedIds) ? stored.unlockedIds : []).filter(
          isKnownGuardianId,
        ),
      ]),
    );
    const selectedId =
      isKnownGuardianId(stored.selectedId) &&
      unlockedIds.includes(stored.selectedId)
        ? stored.selectedId
        : GUARDIAN_NONE_ID;

    dispatch(setGuardianState({unlockedIds, selectedId}));
  } catch (error) {
    console.warn('[Guardians] hydrate failed', error?.message || error);
    // Still mark hydrated: we tried, and the defaults are a valid state.
    dispatch(setGuardianHydrated(true));
  }
};

/**
 * Buy a guardian with QuikCoins.
 *
 * The balance check is a UX guard only - the real debit has to happen
 * server-side, which is what the service stub is a placeholder for.
 *
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const purchaseGuardian =
  ({userId, guardianId, balance}) =>
  async (dispatch, getState) => {
    const guardian = getGuardianById(guardianId);
    if (!guardian) {
      return {success: false, error: `Unknown guardian "${guardianId}"`};
    }

    if (getState().guardians.unlockedIds.includes(guardianId)) {
      return {success: true, alreadyOwned: true};
    }

    const cost = guardian.cost || 0;
    const coins = typeof balance === 'number' ? balance : 0;
    if (cost > coins) {
      const error = `You need ${cost - coins} more QuikCoins to unlock ${guardian.name}.`;
      dispatch(setGuardianError(error));
      return {success: false, error};
    }

    dispatch(setGuardianPurchasing(guardianId));
    dispatch(setGuardianError(null));
    try {
      const resp = await purchaseGuardianApi({userId, guardianId, cost});
      if (!resp?.success) {
        const error = resp?.error || `Could not unlock ${guardian.name}.`;
        dispatch(setGuardianError(error));
        return {success: false, error};
      }
      dispatch(unlockGuardian(guardianId));
      return {success: true, newBalance: resp.newBalance};
    } catch (err) {
      const error = err?.message || `Could not unlock ${guardian.name}.`;
      console.warn('[Guardians] purchase failed', error);
      dispatch(setGuardianError(error));
      return {success: false, error};
    } finally {
      dispatch(setGuardianPurchasing(null));
    }
  };

/**
 * Equip a guardian the user already owns.
 *
 * The state update is applied optimistically so the UI (and the Unity command
 * fired alongside it) responds immediately; a failed save only logs, since
 * Unity mirrors the selection into PlayerPrefs anyway.
 *
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const selectGuardian =
  ({userId, guardianId}) =>
  async (dispatch, getState) => {
    if (!isKnownGuardianId(guardianId)) {
      return {success: false, error: `Unknown guardian "${guardianId}"`};
    }
    if (!getState().guardians.unlockedIds.includes(guardianId)) {
      return {success: false, error: 'That guardian is locked.'};
    }

    dispatch(setSelectedGuardian(guardianId));

    try {
      await saveGuardianSelection({userId, guardianId});
    } catch (err) {
      // Non-fatal: keep the local selection, the save can be retried later.
      console.warn('[Guardians] selection save failed', err?.message || err);
    }
    return {success: true};
  };
